import { renderHtmlToPlainText } from './renderHtmlToPlainText'
import { interpolateVariables, type RenderMode } from './interpolateVariables'
import { appendSignature } from './appendSignature'
import { flattenParagraphsToBreaks } from './flattenParagraphsToBreaks'

export type RenderEmailArgs = {
  bodyHtml: string
  signatureHtml: string
  variables: Record<string, string>
  mode: RenderMode
}

const BR_RUN = /(?:<br\s*\/?>){3,}/gi

/**
 * Prepare a Quill body for an HTML-mode send: flatten `<p>` blocks to `<br>`
 * so paragraphs render single-spaced (the plain-text "one Enter = one line"
 * model), then cap runs of 3+ breaks at one blank line — the `<br>` analogue
 * of the `\n{3,}` -> `\n\n` collapse in renderHtmlToPlainText. Byte-parity with
 * the Python port `render.py:render_html_body`.
 */
export function renderHtmlBody(html: string): string {
  const flattened = flattenParagraphsToBreaks(html).replace(BR_RUN, '<br><br>')
  // NBSP -> regular space, mirroring renderHtmlToPlainText. Quill's
  // getSemanticHTML serializes inter-word spaces as `&nbsp;`, which stops
  // email clients from wrapping lines naturally (text overflows or breaks
  // mid-word instead of at spaces).
  return flattened.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ')
}

/**
 * Render a campaign body + signature to the bytes the recipient will see.
 * Output is byte-equal to the Python port `render_email`. Single entry point
 * shared by every preview surface; the send-path snapshot uses the Python port.
 */
export function renderEmail({
  bodyHtml,
  signatureHtml,
  variables,
  mode,
}: RenderEmailArgs): string {
  const filledBody = interpolateVariables(bodyHtml, variables)
  const renderedBody =
    mode === 'plaintext'
      ? renderHtmlToPlainText(filledBody)
      : renderHtmlBody(filledBody)
  return appendSignature(renderedBody, signatureHtml, mode)
}
